import { ModalViewModel } from '@/infra/ModalViewModel'
import { task } from 'mobx-task'
import type { NavigateFn } from '@/RootStore'
import { links } from '@/utils/link'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { type DraftJobStore } from '@/features/jobs/drafting/DraftJobStore'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'

/**
 * View model for the dialog that deletes a job.
 */
export class DeleteJobDialogViewModel {
  readonly modal = new ModalViewModel()

  constructor(
    public readonly job: Job,
    private readonly _canDeleteJobAsCollaborator: () => boolean,
    private readonly _canDeleteJobAsAdmin: () => boolean,
    private readonly _jobStore: JobStore,
    private readonly _draftJobStore: DraftJobStore,
    private readonly _navigate: NavigateFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Deletes the job and leaves its page.
   */
  delete = task.resolved(async () => {
    const alert = this._newAlert()
      .withState('pending')
      .saying('Deleting job', 'Hang tight!')
      .show()

    try {
      if (this._canDeleteJobAsCollaborator() && !this._canDeleteJobAsAdmin()) {
        await this._draftJobStore.archive(this.job.id)
      } else {
        await this._jobStore.deleteJobAsAdmin(this.job)
      }

      this.modal.close()
      this._navigate(links.jobs())

      alert.withState('success').saying('Success!', 'Job has been deleted!')
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler('Deleting the job failed')(err)
    }
  })
}
